import { ulid } from "ulid";
import {
  isFailOpenSkillsResolveError,
  resolveSkills,
  SkillsClientError,
} from "../client";
import type {
  ResolvedSkillTargetFile,
  SelectedSkillReference,
  SkillDecision,
  SkillOrigin,
  SkillsResolveMode,
  SkillsResolveRequest,
  SkillsResolveResponse,
  SkillsResolveSource,
} from "../types";
import type { FinsterQueryDataSource } from "../../api/finster/constants";
import { mapSkillDecisionFromResolveResponse } from "./skillDecision";

const DEFAULT_SKILL_RESOLVE_TIMEOUT_MS = 60_000;
/**
 * The resolver is a hosted dependency that intermittently rejects with 5xx in
 * a few hundred milliseconds while the very next attempt succeeds. Repeating
 * those attempts inside the existing budget is what stops a flapping upstream
 * from silently dropping a Skill the selector would have matched. The budget
 * is a deadline for the whole operation, so a slow upstream still costs the
 * caller no more wall time than a single attempt used to.
 */
const SKILL_RESOLVE_MAX_ATTEMPTS = 3;
const SKILL_RESOLVE_RETRY_BASE_DELAY_MS = 200;
/** A retry that cannot plausibly land inside the budget only adds latency. */
const SKILL_RESOLVE_MIN_RETRY_BUDGET_MS = 1_000;

export interface SkillSubmissionOptions {
  /** Ordered composer references. Only IDs are sent to the authoritative resolver. */
  selectedSkills?: readonly SelectedSkillReference[];
  /**
   * Whether the user touched the Skill selection for this submission.
   * `true` with no selected Skills is an explicit clear and suppresses inference.
   */
  selectionProvided?: boolean;
}

/**
 * Array form is retained for callers landing concurrently with this plumbing.
 * Its presence is explicit (including `[]`); new UI code should use the object
 * form so untouched empty state remains distinguishable from an explicit clear.
 */
export type SkillSubmissionInput = SkillSubmissionOptions | readonly SelectedSkillReference[];

export interface SkillSubmissionSnapshot {
  readonly selectedSkills: readonly SelectedSkillReference[];
  readonly selectionProvided: boolean;
}

export interface ResolvedRunSkill {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly origin: SkillOrigin;
  readonly description: string;
  readonly dataSourceMode: "all" | "custom";
  readonly dataSourceIds: readonly string[];
  readonly targetFileIds: readonly string[];
}

export interface SkillRunExplorerContext {
  readonly userFileIds: readonly string[] | null;
  readonly userFileTags: readonly string[] | null;
  readonly skillTargetFiles: readonly Readonly<ResolvedSkillTargetFile>[];
}

/**
 * Immutable, prompt-scoped Skill context. It is model input and must remain
 * transient; persisted chat/Review records may retain display refs and counts,
 * but never `guidance`.
 */
export interface SkillRunContext {
  readonly runContextId: string;
  readonly submissionId: string;
  readonly transportVersion: number;
  readonly skillPolicyVersion: number;
  readonly selectionSource: Exclude<SkillsResolveSource, "none">;
  readonly skills: readonly ResolvedRunSkill[];
  /** Exact, opaque guidance returned by data-feed. Never normalize or reconstruct it. */
  readonly guidance: string;
  readonly guidanceChars: number;
  /** `null`, `[]`, and populated arrays retain the endpoint's tri-state semantics. */
  readonly effectiveDataSources: readonly string[] | null;
  readonly effectiveExplorerContext: SkillRunExplorerContext;
}

export type SkillTurnBootstrapStatus =
  | "cleared"
  | "inherited"
  | "resolved"
  | "none"
  | "fail-open"
  | "blocked"
  | "aborted";

export interface SkillResolveFailureSummary {
  readonly kind: string;
  readonly message: string;
  readonly status?: number;
}

export interface SkillResolutionDiagnostics {
  readonly transportVersion: number;
  readonly skillPolicyVersion: number;
  readonly effectiveSourceCount: number | null;
  readonly effectiveFileCount: number | null;
  readonly guidanceChars: number;
  readonly skillCount: number;
}

export interface SkillTurnBootstrapResult {
  readonly status: SkillTurnBootstrapStatus;
  readonly submissionId: string;
  readonly mode: SkillsResolveMode | "inherited";
  readonly context?: SkillRunContext;
  /** Privacy-safe endpoint diagnostics retained even when no Skill matched. */
  readonly resolution?: SkillResolutionDiagnostics;
  /**
   * Matching-skill banner decision for a completed resolve.
   * Null when that resolve is not a matching-skill decision.
   */
  readonly skillDecision?: SkillDecision | null;
  readonly failure?: SkillResolveFailureSummary;
  readonly error?: Error;
}

export interface SkillTurnBootstrapInput {
  readonly submissionId: string;
  readonly userQuery: string;
  readonly dataSources?: readonly FinsterQueryDataSource[];
  readonly userFileIds?: readonly string[];
  readonly userFileTags?: readonly string[];
  readonly selection?: SkillSubmissionInput | SkillSubmissionSnapshot;
  readonly inheritedContext?: SkillRunContext;
  /** Distinguishes an inherited no-Skill/cleared Review binding from no binding. */
  readonly hasInheritedContext?: boolean;
  readonly signal?: AbortSignal;
  readonly timeoutMs?: number;
}

export type SkillsResolveTransport = (
  request: SkillsResolveRequest,
  signal?: AbortSignal
) => Promise<SkillsResolveResponse>;

export interface SkillTurnBootstrapDependencies {
  readonly resolve?: SkillsResolveTransport;
}

class SkillRunContextContractError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SkillRunContextContractError";
    Object.setPrototypeOf(this, SkillRunContextContractError.prototype);
  }
}

class SkillResolveTimeoutError extends Error {
  constructor() {
    super("Skills resolution timed out.");
    this.name = "SkillResolveTimeoutError";
    Object.setPrototypeOf(this, SkillResolveTimeoutError.prototype);
  }
}

class SkillResolveAbortError extends Error {
  constructor() {
    super("Skills resolution was cancelled.");
    this.name = "SkillResolveAbortError";
    Object.setPrototypeOf(this, SkillResolveAbortError.prototype);
  }
}

export function createSkillSubmissionId(): string {
  return ulid();
}

function isSelectedSkillReferenceArray(
  selection: SkillSubmissionInput | undefined
): selection is readonly SelectedSkillReference[] {
  return Array.isArray(selection);
}

export function snapshotSkillSubmissionOptions(
  selection?: SkillSubmissionInput
): SkillSubmissionSnapshot {
  const arraySelection = isSelectedSkillReferenceArray(selection);
  const options = arraySelection ? undefined : selection;
  const selectedSkills = (arraySelection ? selection : (options?.selectedSkills ?? [])).map(
    (skill) =>
      Object.freeze({
        id: skill.id,
        name: skill.name,
        slug: skill.slug,
      })
  );
  return Object.freeze({
    selectedSkills: Object.freeze(selectedSkills),
    // A non-empty selection is necessarily explicit even if an older caller
    // has not yet started forwarding the touched bit.
    selectionProvided:
      arraySelection || options?.selectionProvided === true || selectedSkills.length > 0,
  });
}

function sameOrderedValues(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function freezeStringArray(values: readonly string[]): readonly string[] {
  return Object.freeze([...values]);
}

function buildSkillRunContext(
  response: SkillsResolveResponse,
  mode: SkillsResolveMode,
  explicitSkillIds: readonly string[],
  decision: SkillDecision | null
): SkillRunContext | undefined {
  if (response.skill_ids.length === 0) {
    if (mode === "explicit" && explicitSkillIds.length > 0) {
      throw new SkillRunContextContractError(
        "Skills resolver dropped the ordered explicit selection."
      );
    }
    // A recommended org/system skill is a valid empty applied list. The run
    // continues without guidance; the banner offers Run with.
    if (decision?.kind === "recommended") {
      return undefined;
    }
    if (
      response.source !== "none" ||
      response.skills.length !== 0 ||
      response.guidance !== null ||
      response.guidance_chars !== 0
    ) {
      throw new SkillRunContextContractError(
        "Skills resolver returned inconsistent no-Skill context."
      );
    }
    return undefined;
  }

  if (response.source === "none" || !response.guidance?.trim()) {
    throw new SkillRunContextContractError(
      "Skills resolver returned incomplete active Skill context."
    );
  }
  if (mode === "explicit") {
    if (
      response.source !== "explicit" ||
      !sameOrderedValues(response.skill_ids, explicitSkillIds)
    ) {
      throw new SkillRunContextContractError(
        "Skills resolver changed the ordered explicit selection."
      );
    }
  } else if (response.source !== "inferred") {
    throw new SkillRunContextContractError(
      "Skills resolver returned invalid automatic selection provenance."
    );
  }

  const skills = response.skills.map((skill) =>
    Object.freeze({
      id: skill.id,
      name: skill.name,
      slug: skill.slug,
      origin: skill.origin,
      description: skill.description,
      dataSourceMode: skill.data_source_mode,
      dataSourceIds: freezeStringArray(skill.data_source_ids),
      targetFileIds: freezeStringArray(skill.target_file_ids),
    })
  );
  const targetFiles = response.effective_explorer_context.skill_target_files.map((target) =>
    Object.freeze({ ...target })
  );

  return Object.freeze({
    runContextId: `skill-run-${response.submission_id}`,
    submissionId: response.submission_id,
    transportVersion: response.version,
    skillPolicyVersion: response.skill_policy_version,
    selectionSource: response.source,
    skills: Object.freeze(skills),
    guidance: response.guidance,
    guidanceChars: response.guidance_chars,
    effectiveDataSources:
      response.effective_data_sources === null
        ? null
        : freezeStringArray(response.effective_data_sources),
    effectiveExplorerContext: Object.freeze({
      userFileIds:
        response.effective_explorer_context.user_file_ids === null
          ? null
          : freezeStringArray(response.effective_explorer_context.user_file_ids),
      userFileTags:
        response.effective_explorer_context.user_file_tags === null
          ? null
          : freezeStringArray(response.effective_explorer_context.user_file_tags),
      skillTargetFiles: Object.freeze(targetFiles),
    }),
  });
}

function buildSkillResolutionDiagnostics(
  response: SkillsResolveResponse
): SkillResolutionDiagnostics {
  return Object.freeze({
    transportVersion: response.version,
    skillPolicyVersion: response.skill_policy_version,
    effectiveSourceCount:
      response.effective_data_sources === null ? null : response.effective_data_sources.length,
    effectiveFileCount:
      response.effective_explorer_context.user_file_ids === null
        ? null
        : response.effective_explorer_context.user_file_ids.length,
    guidanceChars: response.guidance_chars,
    skillCount: response.skills.length,
  });
}

function summarizeFailure(error: unknown, fallbackKind: string): SkillResolveFailureSummary {
  if (error instanceof SkillsClientError) {
    return Object.freeze({
      kind: error.kind,
      message: error.message,
      ...(error.status > 0 ? { status: error.status } : {}),
    });
  }
  return Object.freeze({
    kind: fallbackKind,
    message: error instanceof Error ? error.message : String(error),
  });
}

function frozenResult(result: SkillTurnBootstrapResult): SkillTurnBootstrapResult {
  return Object.freeze(result);
}

async function resolveWithTimeout(
  transport: SkillsResolveTransport,
  request: SkillsResolveRequest,
  signal: AbortSignal | undefined,
  timeoutMs: number
): Promise<SkillsResolveResponse> {
  if (signal?.aborted) {
    throw new SkillResolveAbortError();
  }
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let timedOut = false;
  let rejectAbort: ((reason: Error) => void) | undefined;
  const abortPromise = new Promise<never>((_resolve, reject) => {
    rejectAbort = reject;
  });
  const abortForCaller = () => {
    controller.abort();
    rejectAbort?.(new SkillResolveAbortError());
  };

  signal?.addEventListener("abort", abortForCaller, { once: true });
  timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
    rejectAbort?.(new SkillResolveTimeoutError());
  }, timeoutMs);

  try {
    return await Promise.race([transport(request, controller.signal), abortPromise]);
  } catch (error) {
    // Cancelling the transport makes it reject too, and either rejection can
    // win the race. Classify from why we cancelled rather than from whichever
    // error arrived first: the transport reports its own cancellation as
    // `aborted`, which is not a fail-open kind, so a timed-out automatic
    // inference would otherwise block a request the user never scoped to a
    // Skill.
    if (signal?.aborted) throw new SkillResolveAbortError();
    if (timedOut) throw new SkillResolveTimeoutError();
    throw error;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
    signal?.removeEventListener("abort", abortForCaller);
  }
}

/**
 * Only upstream-reported transience earns another attempt. Our own deadline,
 * caller cancellation, contract violations and auth/validation rejections are
 * all terminal, and repeating them would burn budget for a known answer.
 */
function isRetryableResolveError(error: unknown): boolean {
  return (
    error instanceof SkillsClientError &&
    (error.kind === "upstream" || error.kind === "rate_limit" || error.kind === "timeout")
  );
}

function delayBeforeRetry(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new SkillResolveAbortError());
      return;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onAbort = () => {
      clearTimeout(timer);
      reject(new SkillResolveAbortError());
    };
    timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

async function resolveWithRetry(
  transport: SkillsResolveTransport,
  request: SkillsResolveRequest,
  signal: AbortSignal | undefined,
  budgetMs: number
): Promise<SkillsResolveResponse> {
  const deadline = Date.now() + budgetMs;
  for (let attempt = 1; ; attempt += 1) {
    const remaining = deadline - Date.now();
    if (remaining <= 0) throw new SkillResolveTimeoutError();
    try {
      return await resolveWithTimeout(transport, request, signal, remaining);
    } catch (error) {
      const budgetLeft = deadline - Date.now();
      if (
        attempt >= SKILL_RESOLVE_MAX_ATTEMPTS ||
        !isRetryableResolveError(error) ||
        budgetLeft < SKILL_RESOLVE_MIN_RETRY_BUDGET_MS
      ) {
        throw error;
      }
      await delayBeforeRetry(
        Math.min(SKILL_RESOLVE_RETRY_BASE_DELAY_MS * attempt, budgetLeft),
        signal
      );
    }
  }
}

function isAutomaticFailOpenError(error: unknown): boolean {
  return (
    isFailOpenSkillsResolveError(error) ||
    error instanceof SkillRunContextContractError ||
    error instanceof SkillResolveTimeoutError ||
    error instanceof TypeError
  );
}

function fallbackFailureKind(error: unknown): string {
  if (error instanceof SkillResolveTimeoutError) return "timeout";
  if (error instanceof SkillResolveAbortError) return "aborted";
  if (error instanceof TypeError) return "network";
  if (error instanceof SkillRunContextContractError) return "contract";
  return "unknown";
}

export async function resolveSkillTurn(
  input: SkillTurnBootstrapInput,
  dependencies: SkillTurnBootstrapDependencies = {}
): Promise<SkillTurnBootstrapResult> {
  const selection = snapshotSkillSubmissionOptions(input.selection);
  // An active Review owns its submit-time Skill snapshot. An omitted update
  // inherits that snapshot instead of resolving again.
  if (
    !selection.selectionProvided &&
    (input.hasInheritedContext || input.inheritedContext)
  ) {
    return frozenResult({
      status: "inherited",
      submissionId: input.submissionId,
      mode: "inherited",
      context: input.inheritedContext,
    });
  }
  if (selection.selectionProvided && selection.selectedSkills.length === 0) {
    return frozenResult({
      status: "cleared",
      submissionId: input.submissionId,
      mode: "explicit",
    });
  }

  const mode: SkillsResolveMode = selection.selectionProvided ? "explicit" : "automatic";
  const explicitSkillIds = selection.selectedSkills.map((skill) => skill.id);
  const request: SkillsResolveRequest = {
    submission_id: input.submissionId,
    messages: [{ role: "user", content: input.userQuery }],
    ...(mode === "explicit" ? { skill_ids: explicitSkillIds } : {}),
    ...(mode === "automatic" ? { skill_application: "recommend_non_personal" } : {}),
    ...(input.dataSources !== undefined ? { data_sources: [...input.dataSources] } : {}),
    ...(input.userFileIds !== undefined || input.userFileTags !== undefined
      ? {
          explorer_context: {
            ...(input.userFileIds !== undefined ? { user_file_ids: [...input.userFileIds] } : {}),
            ...(input.userFileTags !== undefined
              ? { user_file_tags: [...input.userFileTags] }
              : {}),
          },
        }
      : {}),
  };

  try {
    const response = await resolveWithRetry(
      dependencies.resolve ?? resolveSkills,
      request,
      input.signal,
      input.timeoutMs ?? DEFAULT_SKILL_RESOLVE_TIMEOUT_MS
    );
    const decision = mapSkillDecisionFromResolveResponse(response);
    const context = buildSkillRunContext(response, mode, explicitSkillIds, decision);
    return frozenResult({
      status: context ? "resolved" : "none",
      submissionId: input.submissionId,
      mode,
      ...(context ? { context } : {}),
      skillDecision: decision,
      resolution: buildSkillResolutionDiagnostics(response),
    });
  } catch (error) {
    if (input.signal?.aborted || error instanceof SkillResolveAbortError) {
      return frozenResult({
        status: "aborted",
        submissionId: input.submissionId,
        mode,
        failure: summarizeFailure(error, "aborted"),
        error: error instanceof Error ? error : new Error(String(error)),
      });
    }
    if (mode === "automatic" && isAutomaticFailOpenError(error)) {
      return frozenResult({
        status: "fail-open",
        submissionId: input.submissionId,
        mode,
        failure: summarizeFailure(error, fallbackFailureKind(error)),
      });
    }
    return frozenResult({
      status: "blocked",
      submissionId: input.submissionId,
      mode,
      failure: summarizeFailure(error, fallbackFailureKind(error)),
      error: error instanceof Error ? error : new Error(String(error)),
    });
  }
}

export interface SkillResolveSingleFlight {
  resolveSkillsOnce(input: SkillTurnBootstrapInput): Promise<SkillTurnBootstrapResult>;
  release(submissionId: string): void;
  clear(): void;
}

export function createSkillResolveSingleFlight(
  bootstrap: (
    input: SkillTurnBootstrapInput
  ) => Promise<SkillTurnBootstrapResult> = resolveSkillTurn
): SkillResolveSingleFlight {
  const flights = new Map<string, Promise<SkillTurnBootstrapResult>>();
  return {
    resolveSkillsOnce(input) {
      const existing = flights.get(input.submissionId);
      if (existing) return existing;
      const pending = bootstrap(input);
      flights.set(input.submissionId, pending);
      return pending;
    },
    release(submissionId) {
      flights.delete(submissionId);
    },
    clear() {
      flights.clear();
    },
  };
}
